<template>
  <panel-navigation>

    <h1 class="text-xl font-bold mb-1 text-slate-800">Google Business Profile Manager</h1>
    <hr class="border-b border-gray-200 mb-6">

    <!-- ─── NOT CONNECTED ─── -->
    <div v-if="!connected && !loading" class="gbp-connect-card">
      <div class="gbp-connect-inner">
        <div class="gbp-connect-icon">
          <i class="mdi mdi-google-maps"></i>
        </div>
        <h2 class="gbp-connect-title">Connect Google Business Profile</h2>
        <p class="gbp-connect-sub">Manage your reviews with AI-powered responses and enhance your business photos automatically.</p>
        <div class="gbp-features-grid">
          <div class="gbp-feature-item">
            <i class="mdi mdi-star-outline gbp-feat-icon"></i>
            <span>AI Review Responses</span>
          </div>
          <div class="gbp-feature-item">
            <i class="mdi mdi-bell-outline gbp-feat-icon"></i>
            <span>Instant Notifications</span>
          </div>
          <div class="gbp-feature-item">
            <i class="mdi mdi-image-edit-outline gbp-feat-icon"></i>
            <span>AI Photo Enhancement</span>
          </div>
          <div class="gbp-feature-item">
            <i class="mdi mdi-cloud-upload-outline gbp-feat-icon"></i>
            <span>Auto Photo Upload</span>
          </div>
        </div>
        <button class="gbp-connect-btn" @click="connectGbp">
          <i class="mdi mdi-google mr-2"></i>
          Connect with Google
        </button>
      </div>
    </div>

    <!-- ─── LOADING ─── -->
    <div v-if="loading" class="gbp-loading">
      <v-progress-circular indeterminate color="success" size="48"></v-progress-circular>
      <p class="mt-4 text-slate-500 font-medium">Loading your Business Profile...</p>
    </div>

    <!-- ─── CONNECTED ─── -->
    <div v-if="connected && !loading">

      <!-- Account header bar -->
      <div class="gbp-header-bar">
        <div class="gbp-header-left">
          <div class="gbp-status-dot"></div>
          <div>
            <div class="gbp-account-name">{{ selectedLocationDisplay || selectedAccountDisplay || 'Google Business Profile' }}</div>
            <div class="gbp-account-sub">Connected &amp; Active</div>
          </div>
        </div>
        <div class="gbp-header-right">
          <button class="gbp-settings-btn" @click="settingsDialog = true">
            <i class="mdi mdi-cog-outline mr-1"></i> Settings
          </button>
          <button class="gbp-disconnect-btn" @click="confirmDisconnect">
            <i class="mdi mdi-link-variant-off mr-1"></i> Disconnect
          </button>
        </div>
      </div>

      <!-- Location selector if none chosen -->
      <div v-if="!settings.selected_location_name" class="gbp-setup-card">
        <h3 class="gbp-setup-title"><i class="mdi mdi-map-marker-outline mr-2 text-emerald-500"></i>Select Your Business Location</h3>
        <p class="gbp-setup-sub">Choose which location to manage reviews and photos for.</p>

        <div class="gbp-field-row">
          <div class="gbp-field">
            <label class="gbp-label">Account</label>
            <select class="gbp-select" v-model="selectedAccount" @change="fetchLocations">
              <option value="">-- Select Account --</option>
              <option v-for="acc in accounts" :key="acc.name" :value="acc.name">{{ acc.accountName || acc.name }}</option>
            </select>
          </div>
          <div class="gbp-field" v-if="locations.length > 0">
            <label class="gbp-label">Location</label>
            <select class="gbp-select" v-model="selectedLocation">
              <option value="">-- Select Location --</option>
              <option v-for="loc in locations" :key="loc.name" :value="loc.name">{{ loc.title || loc.name }}</option>
            </select>
          </div>
        </div>
        <button v-if="selectedLocation" class="gbp-save-btn" @click="saveLocation" :disabled="savingSettings">
          <i class="mdi mdi-check mr-1"></i> {{ savingSettings ? 'Saving...' : 'Save Location' }}
        </button>
      </div>

      <!-- Main tabs -->
      <div v-else class="gbp-tabs-wrapper">
        <div class="gbp-tabs">
          <button class="gbp-tab" :class="{ active: activeTab === 'reviews' }" @click="activeTab = 'reviews'">
            <i class="mdi mdi-star-outline mr-2"></i>Reviews
            <span class="gbp-tab-badge" v-if="reviews.length">{{ reviews.length }}</span>
          </button>
          <button class="gbp-tab" :class="{ active: activeTab === 'photos' }" @click="activeTab = 'photos'; loadPhotos()">
            <i class="mdi mdi-image-multiple-outline mr-2"></i>Photos
            <span class="gbp-tab-badge" v-if="photos.length">{{ photos.length }}</span>
          </button>
        </div>

        <!-- ═══ REVIEWS TAB ═══ -->
        <div v-if="activeTab === 'reviews'" class="gbp-tab-content">

          <div class="gbp-reviews-toolbar">
            <div class="gbp-rating-summary" v-if="averageRating">
              <span class="gbp-avg-rating">{{ Number(averageRating).toFixed(1) }}</span>
              <div class="gbp-stars">
                <i v-for="n in 5" :key="n" class="mdi" :class="n <= Math.round(averageRating) ? 'mdi-star' : 'mdi-star-outline'"></i>
              </div>
              <span class="gbp-total-count">{{ totalReviewCount }} reviews</span>
            </div>
            <button class="gbp-refresh-btn" @click="loadReviews" :disabled="loadingReviews">
              <i class="mdi mdi-refresh mr-1" :class="{ 'spin': loadingReviews }"></i>
              {{ loadingReviews ? 'Loading...' : 'Refresh' }}
            </button>
          </div>

          <div v-if="loadingReviews" class="gbp-loading-inline">
            <v-progress-circular indeterminate color="success" size="32"></v-progress-circular>
          </div>

          <div v-if="!loadingReviews && reviews.length === 0" class="gbp-empty-state">
            <i class="mdi mdi-star-circle-outline gbp-empty-icon"></i>
            <p>No reviews found for this location.</p>
          </div>

          <div class="gbp-reviews-list">
            <div v-for="review in reviews" :key="review.reviewId" class="gbp-review-card">
              <div class="gbp-review-header">
                <div class="gbp-reviewer-info">
                  <div class="gbp-reviewer-avatar">
                    <img v-if="review.reviewer && review.reviewer.profilePhotoUrl" :src="review.reviewer.profilePhotoUrl" alt="reviewer">
                    <span v-else>{{ review.reviewer ? (review.reviewer.displayName || '?')[0] : '?' }}</span>
                  </div>
                  <div>
                    <div class="gbp-reviewer-name">{{ review.reviewer ? review.reviewer.displayName : 'Anonymous' }}</div>
                    <div class="gbp-review-date">{{ formatDate(review.createTime) }}</div>
                  </div>
                </div>
                <div class="gbp-star-rating">
                  <i v-for="n in 5" :key="n" class="mdi" :class="n <= starValue(review.starRating) ? 'mdi-star' : 'mdi-star-outline'"></i>
                </div>
              </div>

              <div class="gbp-review-text" v-if="review.comment">{{ review.comment }}</div>
              <div class="gbp-review-text gbp-no-comment" v-else><em>No comment provided.</em></div>

              <!-- Existing reply -->
              <div class="gbp-existing-reply" v-if="review.reviewReply">
                <div class="gbp-reply-label"><i class="mdi mdi-reply mr-1"></i>Your Response</div>
                <div class="gbp-reply-text">{{ review.reviewReply.comment }}</div>
              </div>

              <!-- Response area -->
              <div class="gbp-response-area" v-if="!review.reviewReply || expandedReplies[review.reviewId]">
                <textarea
                  class="gbp-response-textarea"
                  v-model="reviewResponses[review.reviewId]"
                  placeholder="Write your response here..."
                  rows="3"
                ></textarea>
                <div class="gbp-response-actions">
                  <button class="gbp-ai-btn" @click="generateResponse(review)" :disabled="generatingFor === review.reviewId">
                    <i class="mdi mdi-robot-outline mr-1"></i>
                    {{ generatingFor === review.reviewId ? 'Generating...' : 'AI Generate' }}
                  </button>
                  <button class="gbp-post-btn" @click="postResponse(review)" :disabled="postingFor === review.reviewId || !reviewResponses[review.reviewId]">
                    <i class="mdi mdi-send mr-1"></i>
                    {{ postingFor === review.reviewId ? 'Posting...' : 'Post Response' }}
                  </button>
                </div>
              </div>

              <div v-if="review.reviewReply && !expandedReplies[review.reviewId]" class="gbp-edit-reply-link">
                <a href="javascript:void(0)" @click="$set(expandedReplies, review.reviewId, true)">Edit response</a>
              </div>
            </div>
          </div>
        </div>

        <!-- ═══ PHOTOS TAB ═══ -->
        <div v-if="activeTab === 'photos'" class="gbp-tab-content">

          <div class="gbp-photos-toolbar">
            <p class="gbp-photos-desc">Download, AI-enhance, and re-upload your business photos to look more professional.</p>
            <button class="gbp-refresh-btn" @click="loadPhotos" :disabled="loadingPhotos">
              <i class="mdi mdi-refresh mr-1" :class="{ 'spin': loadingPhotos }"></i>
              {{ loadingPhotos ? 'Loading...' : 'Refresh' }}
            </button>
          </div>

          <div v-if="loadingPhotos" class="gbp-loading-inline">
            <v-progress-circular indeterminate color="success" size="32"></v-progress-circular>
          </div>

          <div v-if="!loadingPhotos && photos.length === 0" class="gbp-empty-state">
            <i class="mdi mdi-image-off-outline gbp-empty-icon"></i>
            <p>No photos found for this location.</p>
          </div>

          <div class="gbp-photos-grid">
            <div v-for="(photo, idx) in photos" :key="photo.name" class="gbp-photo-card">
              <div class="gbp-photo-image-wrap">
                <img :src="photo.googleUrl || photo.thumbnailUrl" :alt="photo.locationAssociation && photo.locationAssociation.category" class="gbp-photo-img" />
                <div class="gbp-photo-overlay">
                  <div class="gbp-photo-category">{{ photo.locationAssociation && photo.locationAssociation.category || 'PHOTO' }}</div>
                </div>
              </div>

              <div class="gbp-photo-actions">
                <a :href="photo.googleUrl" target="_blank" class="gbp-photo-action-btn gbp-download-btn" download>
                  <i class="mdi mdi-download mr-1"></i> Download
                </a>
                <button class="gbp-photo-action-btn gbp-enhance-btn"
                  @click="enhancePhoto(photo, idx)"
                  :disabled="enhancingIdx === idx">
                  <i class="mdi mdi-auto-fix mr-1" :class="{ 'spin': enhancingIdx === idx }"></i>
                  {{ enhancingIdx === idx ? 'Enhancing...' : 'AI Enhance' }}
                </button>
              </div>

              <!-- Enhanced version -->
              <div v-if="enhancedPhotos[photo.name]" class="gbp-enhanced-wrap">
                <div class="gbp-enhanced-label"><i class="mdi mdi-auto-fix mr-1"></i>Enhanced Version</div>
                <img :src="enhancedPhotos[photo.name]" alt="Enhanced" class="gbp-photo-img gbp-enhanced-img" />
                <button class="gbp-photo-action-btn gbp-upload-btn"
                  @click="uploadEnhancedPhoto(photo, idx)"
                  :disabled="uploadingIdx === idx">
                  <i class="mdi mdi-cloud-upload-outline mr-1" :class="{ 'spin': uploadingIdx === idx }"></i>
                  {{ uploadingIdx === idx ? 'Uploading...' : 'Upload to GBP' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── SETTINGS DIALOG ─── -->
    <v-dialog v-model="settingsDialog" max-width="520">
      <div class="gbp-dialog">
        <div class="gbp-dialog-header">
          <h3><i class="mdi mdi-cog-outline mr-2 text-emerald-600"></i>GBP Settings</h3>
          <button class="gbp-dialog-close" @click="settingsDialog = false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="gbp-dialog-body">

          <div class="gbp-setting-group">
            <h4 class="gbp-setting-group-title">Review Responses</h4>
            <div class="gbp-toggle-row">
              <div>
                <div class="gbp-toggle-label">Auto-respond with AI</div>
                <div class="gbp-toggle-sub">AI will automatically post responses to new reviews</div>
              </div>
              <v-switch v-model="localSettings.auto_respond_reviews" color="success" hide-details dense></v-switch>
            </div>
          </div>

          <div class="gbp-setting-group">
            <h4 class="gbp-setting-group-title">Notifications</h4>
            <div class="gbp-toggle-row">
              <div>
                <div class="gbp-toggle-label">Email notifications</div>
                <div class="gbp-toggle-sub">Get emailed when a new review comes in</div>
              </div>
              <v-switch v-model="localSettings.notify_new_reviews_email" color="success" hide-details dense></v-switch>
            </div>
            <div class="gbp-toggle-row">
              <div>
                <div class="gbp-toggle-label">SMS notifications</div>
                <div class="gbp-toggle-sub">Get a text when a new review comes in</div>
              </div>
              <v-switch v-model="localSettings.notify_new_reviews_sms" color="success" hide-details dense></v-switch>
            </div>
          </div>

          <div class="gbp-setting-group">
            <h4 class="gbp-setting-group-title">Location</h4>
            <div class="gbp-field">
              <label class="gbp-label">Account</label>
              <select class="gbp-select" v-model="selectedAccount" @change="fetchLocations">
                <option value="">-- Select Account --</option>
                <option v-for="acc in accounts" :key="acc.name" :value="acc.name">{{ acc.accountName || acc.name }}</option>
              </select>
            </div>
            <div class="gbp-field mt-3" v-if="locations.length > 0">
              <label class="gbp-label">Location</label>
              <select class="gbp-select" v-model="selectedLocation">
                <option value="">-- Select Location --</option>
                <option v-for="loc in locations" :key="loc.name" :value="loc.name">{{ loc.title || loc.name }}</option>
              </select>
            </div>
          </div>

        </div>
        <div class="gbp-dialog-footer">
          <button class="gbp-cancel-btn" @click="settingsDialog = false">Cancel</button>
          <button class="gbp-save-settings-btn" @click="saveSettings" :disabled="savingSettings">
            {{ savingSettings ? 'Saving...' : 'Save Settings' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- ─── DISCONNECT CONFIRM ─── -->
    <v-dialog v-model="disconnectDialog" max-width="420">
      <div class="gbp-dialog">
        <div class="gbp-dialog-header">
          <h3><i class="mdi mdi-alert-outline mr-2 text-amber-500"></i>Disconnect GBP?</h3>
          <button class="gbp-dialog-close" @click="disconnectDialog = false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="gbp-dialog-body">
          <p class="text-slate-600">This will remove your Google Business Profile connection. You can reconnect at any time.</p>
        </div>
        <div class="gbp-dialog-footer">
          <button class="gbp-cancel-btn" @click="disconnectDialog = false">Cancel</button>
          <button class="gbp-disconnect-confirm-btn" @click="doDisconnect" :disabled="disconnecting">
            {{ disconnecting ? 'Disconnecting...' : 'Yes, Disconnect' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- ─── SNACKBAR ─── -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
      {{ snackbarMsg }}
    </v-snackbar>

  </panel-navigation>
</template>

<script>
import { mapState } from 'vuex'
import PanelNavigation from '@/components/PanelNavigation.vue'
import axios from 'axios'

export default {
  metaInfo() {
    return { title: 'GBP Manager | ' + this.$appName }
  },

  components: { PanelNavigation },

  computed: {
    ...mapState({ me: state => state.me.me }),

    authHeaders() {
      return {
        headers: {
          'Authorization': 'Bearer ' + this.$store.state.auth.jwt
        }
      }
    },

    selectedAccountDisplay() {
      if (!this.settings || !this.settings.selected_account_name) return ''
      const acc = (this.accounts || []).find(a => a.name === this.settings.selected_account_name)
      return acc ? (acc.accountName || acc.name) : this.settings.selected_account_name
    },

    selectedLocationDisplay() {
      if (!this.settings || !this.settings.selected_location_name) return ''
      const loc = (this.locations || []).find(l => l.name === this.settings.selected_location_name)
      return loc ? (loc.title || loc.name) : this.settings.selected_location_name
    }
  },

  data() {
    return {
      loading: true,
      connected: false,
      settings: {},
      accounts: [],
      locations: [],
      selectedAccount: '',
      selectedLocation: '',

      activeTab: 'reviews',

      // Reviews
      reviews: [],
      averageRating: null,
      totalReviewCount: 0,
      loadingReviews: false,
      reviewResponses: {},
      expandedReplies: {},
      generatingFor: null,
      postingFor: null,

      // Photos
      photos: [],
      loadingPhotos: false,
      enhancedPhotos: {},
      enhancingIdx: null,
      uploadingIdx: null,

      // Settings dialog
      settingsDialog: false,
      localSettings: {},
      savingSettings: false,

      // Disconnect
      disconnectDialog: false,
      disconnecting: false,

      // Snackbar
      snackbar: false,
      snackbarMsg: '',
      snackbarColor: 'success',
    }
  },

  methods: {
    showSnack(msg, color = 'success') {
      this.snackbarMsg = msg
      this.snackbarColor = color
      this.snackbar = true
    },

    connectGbp() {
      const uuid = this.me && this.me.uuid
      const host = window.location.origin
      window.location.href = `${process.env.VUE_APP_API_ENDPOINT}/auth/google?jwt=${uuid}&gbp=1&host=${host}`
    },

    async loadIntegration() {
      this.loading = true
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/gbp/integration`, this.authHeaders)
        this.connected = res.data.connected
        this.settings = res.data.settings || {}
        this.localSettings = { ...this.settings }

        if (this.connected) {
          this.accounts = this.settings.accounts || []
          this.selectedAccount = this.settings.selected_account_name || ''
          this.selectedLocation = this.settings.selected_location_name || ''

          if (this.settings.selected_account_name && this.locations.length === 0) {
            await this.fetchLocations()
          }

          if (this.settings.selected_location_name) {
            this.loadReviews()
          }
        }
      } catch (e) {
        console.error('GBP load error', e)
      } finally {
        this.loading = false
      }
    },

    async fetchLocations() {
      if (!this.selectedAccount) return
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/gbp/locations`, {
          params: { account: this.selectedAccount },
          ...this.authHeaders
        })
        this.locations = res.data.locations || []
      } catch (e) {
        this.showSnack('Failed to load locations', 'error')
      }
    },

    async saveLocation() {
      this.savingSettings = true
      try {
        await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/gbp/save-settings`, {
          selected_account_name: this.selectedAccount,
          selected_location_name: this.selectedLocation,
        }, this.authHeaders)
        this.settings.selected_account_name = this.selectedAccount
        this.settings.selected_location_name = this.selectedLocation
        this.showSnack('Location saved!')
        this.loadReviews()
      } catch (e) {
        this.showSnack('Failed to save location', 'error')
      } finally {
        this.savingSettings = false
      }
    },

    async saveSettings() {
      this.savingSettings = true
      try {
        await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/gbp/save-settings`, {
          selected_account_name: this.selectedAccount || this.settings.selected_account_name,
          selected_location_name: this.selectedLocation || this.settings.selected_location_name,
          auto_respond_reviews: this.localSettings.auto_respond_reviews,
          notify_new_reviews_email: this.localSettings.notify_new_reviews_email,
          notify_new_reviews_sms: this.localSettings.notify_new_reviews_sms,
        }, this.authHeaders)
        this.settings = { ...this.settings, ...this.localSettings }
        this.settingsDialog = false
        this.showSnack('Settings saved!')
      } catch (e) {
        this.showSnack('Failed to save settings', 'error')
      } finally {
        this.savingSettings = false
      }
    },

    confirmDisconnect() {
      this.disconnectDialog = true
    },

    async doDisconnect() {
      this.disconnecting = true
      try {
        await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/gbp/disconnect`, {}, this.authHeaders)
        this.connected = false
        this.settings = {}
        this.reviews = []
        this.photos = []
        this.disconnectDialog = false
        this.showSnack('Disconnected from Google Business Profile')
      } catch (e) {
        this.showSnack('Failed to disconnect', 'error')
      } finally {
        this.disconnecting = false
      }
    },

    async loadReviews() {
      this.loadingReviews = true
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/gbp/reviews`, this.authHeaders)
        this.reviews = res.data.reviews || []
        this.averageRating = res.data.averageRating
        this.totalReviewCount = res.data.totalReviewCount || 0
      } catch (e) {
        this.showSnack('Failed to load reviews', 'error')
      } finally {
        this.loadingReviews = false
      }
    },

    async generateResponse(review) {
      this.generatingFor = review.reviewId
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/gbp/generate-review-response`, {
          review_text: review.comment || '',
          star_rating: review.starRating,
          business_name: this.me && this.me.company_name || '',
        }, this.authHeaders)
        this.$set(this.reviewResponses, review.reviewId, res.data.response)
        this.showSnack('AI response generated!')
      } catch (e) {
        this.showSnack('Failed to generate response', 'error')
      } finally {
        this.generatingFor = null
      }
    },

    async postResponse(review) {
      const replyText = this.reviewResponses[review.reviewId]
      if (!replyText) return
      this.postingFor = review.reviewId
      try {
        await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/gbp/post-review-response`, {
          review_name: review.name,
          reply_text: replyText,
        }, this.authHeaders)
        this.$set(review, 'reviewReply', { comment: replyText })
        this.$set(this.reviewResponses, review.reviewId, '')
        this.$set(this.expandedReplies, review.reviewId, false)
        this.showSnack('Response posted successfully!')
      } catch (e) {
        this.showSnack('Failed to post response', 'error')
      } finally {
        this.postingFor = null
      }
    },

    async loadPhotos() {
      if (this.loadingPhotos) return
      this.loadingPhotos = true
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/gbp/photos`, this.authHeaders)
        this.photos = res.data.photos || []
      } catch (e) {
        this.showSnack('Failed to load photos', 'error')
      } finally {
        this.loadingPhotos = false
      }
    },

    async enhancePhoto(photo, idx) {
      this.enhancingIdx = idx
      this.showSnack('Enhancing photo with AI — this may take up to 30 seconds...', 'info')
      try {
        const res = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/gbp/enhance-photo`, {
          photo_url: photo.googleUrl || photo.thumbnailUrl,
        }, this.authHeaders)
        this.$set(this.enhancedPhotos, photo.name, res.data.enhanced_url)
        this.showSnack('Photo enhanced! Review and upload it.')
      } catch (e) {
        this.showSnack('Failed to enhance photo', 'error')
      } finally {
        this.enhancingIdx = null
      }
    },

    async uploadEnhancedPhoto(photo, idx) {
      const enhancedUrl = this.enhancedPhotos[photo.name]
      if (!enhancedUrl) return
      this.uploadingIdx = idx
      try {
        await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/gbp/upload-photo`, {
          photo_url: enhancedUrl,
          category: photo.locationAssociation && photo.locationAssociation.category || 'ADDITIONAL',
        }, this.authHeaders)
        this.showSnack('Photo uploaded to GBP!')
        this.$set(this.enhancedPhotos, photo.name, null)
      } catch (e) {
        this.showSnack('Failed to upload photo', 'error')
      } finally {
        this.uploadingIdx = null
      }
    },

    starValue(rating) {
      const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 }
      return map[rating] || 0
    },

    formatDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
  },

  created() {
    this.loadIntegration()
  },

  watch: {
    settingsDialog(val) {
      if (val) {
        this.localSettings = { ...this.settings }
        this.selectedAccount = this.settings.selected_account_name || ''
        this.selectedLocation = this.settings.selected_location_name || ''
        if (this.selectedAccount && this.locations.length === 0) {
          this.fetchLocations()
        }
      }
    }
  }
}
</script>

<style scoped>
/* ── Connect Card ── */
.gbp-connect-card {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 2rem;
}
.gbp-connect-inner {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 3rem 3.5rem;
  text-align: center;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.gbp-connect-icon {
  font-size: 3.5rem;
  color: #10b981;
  margin-bottom: 1.25rem;
}
.gbp-connect-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}
.gbp-connect-sub {
  color: #64748b;
  font-size: 0.95rem;
  margin-bottom: 2rem;
  line-height: 1.5;
}
.gbp-features-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  margin-bottom: 2rem;
}
.gbp-feature-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 500;
}
.gbp-feat-icon {
  color: #10b981;
  font-size: 1.1rem;
}
.gbp-connect-btn {
  background: #10b981;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.gbp-connect-btn:hover {
  background: #059669;
}

/* ── Loading ── */
.gbp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}
.gbp-loading-inline {
  display: flex;
  justify-content: center;
  padding: 3rem;
}

/* ── Header bar ── */
.gbp-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}
.gbp-header-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}
.gbp-status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16,185,129,0.15);
}
.gbp-account-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1e293b;
}
.gbp-account-sub {
  font-size: 0.75rem;
  color: #10b981;
  font-weight: 500;
  margin-top: 1px;
}
.gbp-header-right {
  display: flex;
  gap: 0.6rem;
}
.gbp-settings-btn {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0.4rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}
.gbp-settings-btn:hover { background: #e2e8f0; }
.gbp-disconnect-btn {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 0.4rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}
.gbp-disconnect-btn:hover { background: #fee2e2; }

/* ── Setup Card ── */
.gbp-setup-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.75rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}
.gbp-setup-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.35rem;
}
.gbp-setup-sub {
  color: #64748b;
  font-size: 0.85rem;
  margin-bottom: 1.25rem;
}
.gbp-field-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}
.gbp-field { flex: 1; }
.gbp-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.35rem;
}
.gbp-select {
  width: 100%;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.55rem 0.75rem;
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.2s;
}
.gbp-select:focus { border-color: #10b981; }
.gbp-select option { background: #ffffff; color: #1e293b; }
.gbp-save-btn, .gbp-save-settings-btn {
  background: #10b981;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.55rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.gbp-save-btn:hover, .gbp-save-settings-btn:hover { background: #059669; }
.gbp-save-btn:disabled, .gbp-save-settings-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Tabs ── */
.gbp-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 1.5rem;
}
.gbp-tab {
  background: transparent;
  border: none;
  color: #64748b;
  padding: 0.65rem 1.25rem;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}
.gbp-tab.active {
  color: #10b981;
  border-bottom-color: #10b981;
}
.gbp-tab:hover:not(.active) { color: #334155; }
.gbp-tab-badge {
  background: #e2e8f0;
  color: #334155;
  border-radius: 50px;
  padding: 0 0.45rem;
  font-size: 0.72rem;
  margin-left: 0.5rem;
  font-weight: 600;
}

/* ── Reviews ── */
.gbp-reviews-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.gbp-rating-summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.gbp-avg-rating {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}
.gbp-stars { color: #f59e0b; font-size: 0.95rem; }
.gbp-total-count { color: #64748b; font-size: 0.85rem; }
.gbp-refresh-btn {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0.45rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}
.gbp-refresh-btn:hover { background: #e2e8f0; }
.gbp-refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.gbp-empty-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #94a3b8;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.gbp-empty-icon { font-size: 2.5rem; display: block; margin-bottom: 0.75rem; color: #cbd5e1; }

.gbp-reviews-list { display: flex; flex-direction: column; gap: 1rem; }
.gbp-review-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}
.gbp-review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.gbp-reviewer-info { display: flex; align-items: center; gap: 0.75rem; }
.gbp-reviewer-avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex; align-items: center; justify-content: center;
  color: #475569;
  font-weight: 600;
  font-size: 0.9rem;
  overflow: hidden;
}
.gbp-reviewer-avatar img { width: 100%; height: 100%; object-fit: cover; }
.gbp-reviewer-name { font-weight: 600; font-size: 0.9rem; color: #1e293b; }
.gbp-review-date { font-size: 0.78rem; color: #64748b; }
.gbp-star-rating { color: #f59e0b; font-size: 0.95rem; }
.gbp-review-text { color: #334155; font-size: 0.88rem; line-height: 1.5; margin-bottom: 1rem; }
.gbp-no-comment { font-style: italic; color: #94a3b8; }

.gbp-existing-reply {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}
.gbp-reply-label { font-size: 0.78rem; font-weight: 600; color: #059669; margin-bottom: 0.35rem; }
.gbp-reply-text { font-size: 0.85rem; color: #334155; line-height: 1.5; }
.gbp-edit-reply-link { margin-top: 0.4rem; font-size: 0.82rem; }
.gbp-edit-reply-link a { color: #10b981; font-weight: 500; text-decoration: none; }
.gbp-edit-reply-link a:hover { text-decoration: underline; }

.gbp-response-area { margin-top: 0.5rem; }
.gbp-response-textarea {
  width: 100%;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.65rem 0.85rem;
  font-size: 0.85rem;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
  font-family: inherit;
}
.gbp-response-textarea:focus { border-color: #10b981; }
.gbp-response-actions {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.5rem;
}
.gbp-ai-btn {
  background: #f0fdf4;
  color: #059669;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.gbp-ai-btn:hover { background: #dcfce7; }
.gbp-ai-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gbp-post-btn {
  background: #10b981;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.gbp-post-btn:hover { background: #059669; }
.gbp-post-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Photos ── */
.gbp-photos-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.gbp-photos-desc { color: #64748b; font-size: 0.85rem; }
.gbp-photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.25rem;
}
.gbp-photo-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}
.gbp-photo-image-wrap { position: relative; }
.gbp-photo-img { width: 100%; height: 160px; object-fit: cover; display: block; }
.gbp-photo-overlay {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  padding: 0.4rem 0.75rem;
  background: linear-gradient(transparent, rgba(0,0,0,0.6));
}
.gbp-photo-category { font-size: 0.7rem; color: #ffffff; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
.gbp-photo-actions {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
}
.gbp-photo-action-btn {
  flex: 1;
  text-align: center;
  padding: 0.4rem 0.4rem;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.gbp-download-btn {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
}
.gbp-download-btn:hover { background: #e2e8f0; }
.gbp-enhance-btn {
  background: #f0fdf4;
  color: #059669;
  border: 1px solid #bbf7d0;
}
.gbp-enhance-btn:hover { background: #dcfce7; }
.gbp-enhance-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gbp-upload-btn {
  background: #10b981;
  color: #fff;
  width: calc(100% - 1.5rem);
  margin: 0 0.75rem 0.75rem;
}
.gbp-upload-btn:hover { background: #059669; }
.gbp-upload-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gbp-enhanced-wrap {
  border-top: 1px solid #e2e8f0;
  padding-top: 0.5rem;
}
.gbp-enhanced-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
  padding: 0.4rem 0.75rem 0.3rem;
}
.gbp-enhanced-img { border-top: 1px solid #e2e8f0; }

/* ── Dialog ── */
.gbp-dialog {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
}
.gbp-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.15rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}
.gbp-dialog-header h3 {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}
.gbp-dialog-close {
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 1.2rem;
  cursor: pointer;
}
.gbp-dialog-close:hover { color: #1e293b; }
.gbp-dialog-body { padding: 1.25rem; }
.gbp-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.gbp-cancel-btn {
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
}
.gbp-cancel-btn:hover { background: #f1f5f9; }
.gbp-disconnect-confirm-btn {
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}
.gbp-disconnect-confirm-btn:hover { background: #b91c1c; }
.gbp-disconnect-confirm-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Setting groups ── */
.gbp-setting-group { margin-bottom: 1.5rem; }
.gbp-setting-group:last-child { margin-bottom: 0; }
.gbp-setting-group-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #059669;
  margin-bottom: 0.65rem;
}
.gbp-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f1f5f9;
}
.gbp-toggle-row:last-child { border-bottom: none; }
.gbp-toggle-label { font-size: 0.88rem; color: #1e293b; font-weight: 500; }
.gbp-toggle-sub { font-size: 0.75rem; color: #64748b; margin-top: 1px; }
.mt-3 { margin-top: 0.75rem; }

/* ── Spin animation ── */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.spin { display: inline-block; animation: spin 1s linear infinite; }
</style>